import { GenericObjectType } from "@rjsf/utils";

export const objectKeys = <Obj extends object>(obj: Obj) => Object.keys(obj) as (keyof Obj)[];

export const objectValues = <Obj extends object>(obj: Obj) =>
  Object.values(obj) as Obj[keyof Obj][];

export const objectEntries = <Obj extends object>(obj: Obj) =>
  Object.entries(obj) as [keyof Obj, Obj[keyof Obj]][];

export const objectFromEntries = <Key extends string | number | symbol, Value>(
  obj: [Key, Value][]
) => Object.fromEntries(obj) as Record<Key, Value>;

export const isPlainObject = (value: unknown): value is object =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// Combine multiple objects into one, merging nested objects instead of overwriting them.
// The first object in the list has the lowest priority, the last object has the highest priority.
// Primitives and arrays are overwritten by higher-priority values, not merged.
export const combineObjects = <
  Combined extends object,
  Parts extends Partial<Combined>[] = Partial<Combined>[]
>(
  ...objects: Parts
): Combined => {
  const result: Record<string | number | symbol, unknown> = {};

  for (const obj of objects) {
    for (const [key, value] of objectEntries(obj)) {
      // Undefined means "no value provided" and should not overwrite an existing value.
      if (value === undefined) continue;

      const existing = result[key];

      if (isPlainObject(existing) && isPlainObject(value)) {
        result[key] = { ...existing, ...value };
      } else {
        result[key] = value;
      }
    }
  }

  return result as Combined;
};

export const removeNullUndefinedObj = <T extends object>(obj: T): Partial<T> => {
  const result: Partial<T> = {};

  for (const [key, value] of objectEntries(obj)) {
    if (value === undefined || value === null) continue;
    result[key] = value as T[keyof T];
  }

  return result;
};

export const removeUndefinedObj = <T extends object>(obj: T): Partial<T> => {
  const result: Partial<T> = {};

  for (const [key, value] of objectEntries(obj)) {
    if (value === undefined) continue;
    result[key] = value as T[keyof T];
  }

  return result;
};

const snakeToCamel = (s: string) => s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
const camelToSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const convertKeys =
  (transform: (key: string) => string) =>
  (value: GenericObjectType | undefined): GenericObjectType | undefined => {
    if (Array.isArray(value)) return value.map(convertKeys(transform));
    if (value !== null && typeof value === "object")
      return Object.fromEntries(
        Object.entries(value as Record<string, GenericObjectType>).map(([k, v]) => [
          transform(k),
          convertKeys(transform)(v),
        ])
      );
    return value;
  };
export const snakeToCamelDeep = convertKeys(snakeToCamel);
export const camelToSnakeDeep = convertKeys(camelToSnake);
